/**
 * BFS-based hierarchical layout for ReactFlow module graphs.
 *
 * Produces strict left-to-right column positions where column index = BFS depth
 * from the start module, with barycenter ordering to minimize edge crossings.
 */

const NODE_WIDTH = 260;
const NODE_HEIGHT = 140;
const COLUMN_GAP = 120;
const ROW_GAP = 60;
const MARGIN_X = 40;
const MARGIN_Y = 40;

/**
 * Build forward and reverse adjacency maps from all edge types.
 */
function buildAdjacency(modules) {
    const forward = {};  // outgoing edges
    const reverse = {};  // incoming edges

    modules.forEach((mod) => {
        const id = String(mod.id);
        if (!forward[id]) forward[id] = [];
        if (!reverse[id]) reverse[id] = [];
    });

    modules.forEach((mod) => {
        const sourceId = String(mod.id);

        // Connections
        (mod.connection || []).forEach((conn) => {
            if (!conn.goto) return;
            const targetId = String(conn.goto);
            if (!forward[targetId]) return; // target not in modules
            forward[sourceId].push(targetId);
            reverse[targetId].push(sourceId);
        });

        // Fallback
        if (mod.fallback) {
            const targetId = String(mod.fallback);
            if (forward[targetId]) {
                forward[sourceId].push(targetId);
                reverse[targetId].push(sourceId);
            }
        }

        // Triggers
        (mod.triggers || []).forEach((trigger) => {
            if (trigger.deleted_at) return;
            if (!trigger.goto_module_id) return;
            const targetId = String(trigger.goto_module_id);
            if (forward[targetId]) {
                forward[sourceId].push(targetId);
                reverse[targetId].push(sourceId);
            }
        });
    });

    return { forward, reverse };
}

/**
 * BFS depth assignment from a seed node.
 * Assigns minimum depth (shortest path from seed).
 */
function assignDepths(seedId, initialDepth, depthById, forward) {
    if (!seedId || !forward[seedId]) return;

    const queue = [seedId];
    depthById[seedId] = depthById[seedId] === undefined
        ? initialDepth
        : Math.min(depthById[seedId], initialDepth);

    while (queue.length) {
        const currentId = queue.shift();
        const currentDepth = depthById[currentId];
        const nextDepth = currentDepth + 1;

        (forward[currentId] || []).forEach((targetId) => {
            if (depthById[targetId] === undefined || nextDepth < depthById[targetId]) {
                depthById[targetId] = nextDepth;
                queue.push(targetId);
            }
        });
    }
}

/**
 * Barycenter ordering: sort nodes within each column by the average
 * row position of their neighbors in the previous column.
 * This minimizes edge crossings in a single left-to-right pass.
 */
function barycentricOrder(columns, reverse, depthById) {
    const rowIndex = {}; // moduleId -> row index within its column

    // Column 0: keep original order (start module first)
    const sortedColumns = Array.from(columns.entries())
        .sort((a, b) => a[0] - b[0]);

    sortedColumns.forEach(([depth, ids], colIdx) => {
        if (colIdx === 0) {
            // First column — no reordering needed
            ids.forEach((id, idx) => { rowIndex[id] = idx; });
            return;
        }

        // Compute barycenter for each node
        const scored = ids.map((id, originalIdx) => {
            const neighbors = (reverse[id] || []).filter(
                (nid) => depthById[nid] === depth - 1
            );

            if (neighbors.length === 0) {
                // No neighbors in previous column — use large value to push to bottom
                return { id, score: 1000 + originalIdx };
            }

            const avgRow = neighbors.reduce(
                (sum, nid) => sum + (rowIndex[nid] || 0), 0
            ) / neighbors.length;

            return { id, score: avgRow };
        });

        scored.sort((a, b) => a.score - b.score);

        // Update ids in place and record row indices
        scored.forEach((item, idx) => {
            ids[idx] = item.id;
            rowIndex[item.id] = idx;
        });
    });

    return rowIndex;
}

/**
 * Main layout function.
 *
 * @param {Array} modules - Array of module objects with id, connection, fallback, triggers
 * @param {string|null} startModuleId - ID of the start module (leftmost)
 * @returns {Object} Map of { [moduleId]: { x, y } }
 */
export default function layoutModules(modules, startModuleId) {
    if (!modules || !modules.length) return {};

    const { forward, reverse } = buildAdjacency(modules);

    // Resolve start module
    const resolvedStart = startModuleId && forward[String(startModuleId)]
        ? String(startModuleId)
        : String(modules[0].id);

    // BFS depth assignment
    const depthById = {};
    assignDepths(resolvedStart, 0, depthById, forward);

    // Handle disconnected modules
    modules.forEach((mod) => {
        const id = String(mod.id);
        if (depthById[id] === undefined) {
            const maxDepth = Object.keys(depthById).length
                ? Math.max(...Object.values(depthById))
                : 0;
            assignDepths(id, maxDepth + 1, depthById, forward);
        }
    });

    // Group into columns by depth
    const columns = new Map();
    modules.forEach((mod) => {
        const id = String(mod.id);
        const depth = depthById[id] || 0;
        if (!columns.has(depth)) columns.set(depth, []);
        columns.get(depth).push(id);
    });

    // Barycenter ordering to minimize crossings
    const rowIndex = barycentricOrder(columns, reverse, depthById);

    // Calculate max column size for vertical centering
    const maxRows = Math.max(1, ...Array.from(columns.values()).map((ids) => ids.length));

    // Compute positions
    const positions = {};
    Array.from(columns.entries()).forEach(([depth, ids]) => {
        const offsetY = (maxRows - ids.length) * (NODE_HEIGHT + ROW_GAP) / 2;
        ids.forEach((id, idx) => {
            positions[id] = {
                x: MARGIN_X + depth * (NODE_WIDTH + COLUMN_GAP),
                y: MARGIN_Y + offsetY + idx * (NODE_HEIGHT + ROW_GAP),
            };
        });
    });

    return positions;
}
